import { useEffect, useRef } from 'react';
import { Draggable } from '@fullcalendar/interaction';

import { cn } from '@/lib/cn';
import { BLOCKER_KINDS, type BlockerKind } from './types';

interface Props {
  onPick: (kind: BlockerKind) => void;
}

/**
 * Drag sources for unavailable time.
 *
 * Separate from the backlog on purpose: these are not work waiting to be done,
 * and mixing them into the same list would put "Lunch" in a queue of things
 * you are behind on.
 */
export function BlockerPalette({ onPick }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const draggable = new Draggable(el, {
      itemSelector: '[data-blocker-chip]',
      eventData(chip) {
        return {
          title: chip.dataset.title ?? 'Blocker',
          duration: { minutes: Number(chip.dataset.minutes) || 30 },
          create: false,
        };
      },
    });
    return () => draggable.destroy();
  }, []);

  return (
    <div ref={ref}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="text-small font-medium text-text-secondary">Blockers</h2>
        <span className="text-label text-text-tertiary">busy, not work</span>
      </div>

      {/* Even columns rather than a wrapping pill flow: five differently-sized
          pills in a narrow sidebar collide into one grey blob. */}
      <div className="grid grid-cols-2 gap-2">
        {BLOCKER_KINDS.map((kind, index) => {
          const name = kind.paletteLabel ?? kind.label;
          return (
            <div
              key={kind.value}
              data-blocker-chip
              data-blocker-kind={kind.value}
              data-title={kind.label}
              data-minutes={kind.minutes}
              role="button"
              tabIndex={0}
              aria-label={`${name} blocker, ${kind.minutes} minutes. Drag onto the calendar, or press Enter to place one.`}
              onClick={() => onPick(kind.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPick(kind.value);
                }
              }}
              className={cn(
                'plan-blocker-chip flex cursor-grab flex-col justify-center gap-0.5',
                'rounded border border-dashed border-border bg-surface-2 px-3 py-2',
                'text-small text-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'hover:border-border-strong hover:text-text active:cursor-grabbing',
                // Odd count: the last one takes the empty column rather than
                // leaving a hole.
                index === BLOCKER_KINDS.length - 1 &&
                  BLOCKER_KINDS.length % 2 === 1 &&
                  'col-span-2',
              )}
            >
              <span className="truncate leading-tight">{name}</span>
              <span className="text-label leading-none tabular text-text-tertiary">
                {kind.minutes}m
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
